/** @jsx jsx */
import { useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { TablePagination } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { ICustomTheme } from 'interfaces';
import euroIcon from 'assets/icons/euro.svg';
import numbersIcon from 'assets/icons/nombres.svg';
import ProductThumbnail from '../ProductsThumbnail';
import Value from '../Value';
import { IProductsProps } from '..';
import {
  StyledPagination,
  StyledProductContainer,
  StyledProductInfo,
  StyledProductsContainer,
} from './styles';
import { TablePaginationActions } from 'components';
import { useTheme } from 'emotion-theming';
import { TProductDetails } from 'interfaces/challenge.types';

export const ProductsDetailsListColumnRendering = ({
  productDetailsList,
  rowsPerPage,
  isGoalMecanicByProduct,
  displayFinancialValues,
  displayGainValues,
  unitToDisplay,
  withHeaderFieldsDescription,
}: IProductsProps): JSX.Element => {
  const [page, setPage] = useState(0);

  const { t } = useTranslation();

  const theme: ICustomTheme = useTheme();

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const isPaginationEnabled =
    rowsPerPage && productDetailsList.length > rowsPerPage;

  const productsInPage = useMemo(() => {
    return rowsPerPage && isPaginationEnabled
      ? productDetailsList.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        )
      : productDetailsList;
  }, [isPaginationEnabled, rowsPerPage, page, productDetailsList]);

  return (
    <StyledProductsContainer>
      {withHeaderFieldsDescription && (
        <StyledProductContainer
          backgroundColor={theme.colors.grey.light5}
          fontColor={theme.colors.grey.light1}
          isItalicStyleText
        >
          {t('challenges.declaration.history.item', {
            count: productsInPage.length,
          })}
        </StyledProductContainer>
      )}
      {productsInPage.map((product: TProductDetails) => {
        const thresholdText =
          product.threshold && product.threshold > 1
            ? `${product.reward} ${unitToDisplay}${t(
                'challenges.declaration.thresholdUnit',
              )} ${t('challenges.declaration.thresholdTitle', {
                threshold: product.threshold,
              })}`
            : `${product.reward} ${unitToDisplay}${t(
                'challenges.declaration.thresholdUnit',
              )}`;
        const coefficientText =
          product.coefficient &&
          `${product.coefficient} ${unitToDisplay}${t(
            'challenges.declaration.thresholdUnit',
          )}`;
        return (
          <StyledProductContainer>
            <ProductThumbnail
              css={{ margin: 0, width: 100, height: 100 }}
              thumbnail={product.visual}
            />
            <StyledProductInfo>
              <div>{product.unit}</div>
              <div>
                <strong>{product.brand}</strong>
                {product.brand && <span> - </span>}
                {product.productName}
              </div>
              <div>{product.productCode}</div>
              {isGoalMecanicByProduct ? (
                <div>{thresholdText}</div>
              ) : (
                <div>{coefficientText}</div>
              )}
              {product.quantity !== null && (
                <Value
                  value={product.quantity}
                  imgProps={{ src: numbersIcon, alt: 'quantity icon' }}
                  textProps={{
                    color: 'secondary',
                    fontSize: 16,
                  }}
                />
              )}
              {displayFinancialValues && product.financialValue !== null && (
                <Value
                  value={product.financialValue}
                  imgProps={{ src: euroIcon, alt: 'euro icon' }}
                  textProps={{ color: 'primary' }}
                />
              )}
              {displayGainValues && isGoalMecanicByProduct && (
                <div>
                  <Value
                    value={product.productRewardValue}
                    textProps={{ color: theme.colors.paletteFromBO.main }}
                  />{' '}
                  {unitToDisplay}*
                </div>
              )}
              {displayGainValues && !isGoalMecanicByProduct && (
                <div>
                  <Value
                    value={product.productResultValue}
                    textProps={{ color: theme.colors.paletteFromBO.main }}
                  />{' '}
                  {unitToDisplay}
                </div>
              )}
            </StyledProductInfo>
          </StyledProductContainer>
        );
      })}
      {rowsPerPage && isPaginationEnabled && (
        <StyledPagination>
          <TablePagination
            rowsPerPageOptions={[]}
            page={page}
            count={productDetailsList.length}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            ActionsComponent={TablePaginationActions}
          />
        </StyledPagination>
      )}
    </StyledProductsContainer>
  );
};
